/**
 * The chrome every signed-in page is mounted inside: the brand, the campaign
 * switcher, the capability-driven navigation, and an outlet for the route.
 *
 * The nav entries arrive as a prop rather than being imported. `routes/index.ts`
 * discovers the feature areas and passes what it found, which keeps the import
 * running one way -- the composition depends on the shell, the shell does not
 * depend on the composition -- and lets a test mount the shell with a synthetic
 * bundle without a module registry to reset between cases.
 */

import { useEffect, useState, type ReactNode } from 'react';
import {
  interpolatePath,
  Link,
  Outlet,
  useLocation,
  useMatchRoute,
  useParams,
} from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { AccountSettings, Shell, UserFooter, type Crumb } from '../../chrome/shell';
import { activeEntry, matchesRoute } from '../../chrome/shell/topbar';
import { getLocale, setLocale, t, type Locale } from '../../i18n';
import { credits as formatCredits } from '../../i18n/format';
import { Button } from '../../ui/button';
import { Toaster } from '../../ui/sonner';
import type { NavEntry } from '../routes/contract';
import { tt } from '../tenant/i18n';
import { acquisitionState } from '../tenant/balance/state';
import { NoOpenRequest } from '../tenant/capacity/NoOpenRequest';
import { formatDate } from '../tenant/balance/FundedWindowCard';
import { useAcquisitionState, useCredits } from '../tenant/balance/useCredits';
import { useFundedWindow } from '../tenant/balance/useFundedWindow';
import { CreditsStatusIcon, MonthCreditsMeter } from '../tenant/balance/MonthCreditsMeter';
import { useAuthState, useSignOut } from '../session/authState';
import { firebaseDisplayName, firebaseProvider, resolveDisplayName } from '../session/identity';
import { writeLastPage } from '../session/lastPage';
import { writeLastUsed } from '../session/lastUsed';
import { useMe } from '../session/session';
import { organisationHref, useOrgContext } from '../session/orgContext';
import { NotYourOrganisation } from '../session/switcher/NotYourOrganisation';
import { OrganisationSwitcher } from '../session/switcher/OrganisationSwitcher';
import { cm } from './creditsMessages';
import { Nav, useVisibleNavEntries } from './Nav';
import { sessionGate } from './Forbidden';

/** The sidebar's persistent balance card (Figma `122:2`, `247:10768`):
 *  how much of the tenant's credits is still available to spend, the same
 *  figure `BalanceStatGrid` leads with, and the coverage caption below it --
 *  `funded_until`, the same field `FundedWindowCard`'s "window.until" reads.
 *  Blocked (`acquisitionState` `waiting-on-credits`) swaps the figure for
 *  `meter.empty` and the caption for when collection stopped, rather than a
 *  figure that reads zero. Silent while `credits` is loading or on any
 *  failure -- the same reticence `FundedWindowCard` already uses for a query
 *  this shell does not control the retry of, rather than a spinner or an
 *  error card competing with the page it sits beside.
 *
 *  The caption withholds independently: a pending funded-window query is
 *  `undefined`, distinct from a landed, zero-length window
 *  (`funded_until: null`) -- both stay silent, same as `AcquisitionStatus`'s
 *  own `running` branch, which already renders the balance from `credits`
 *  alone while the window is unconfirmed rather than waiting on it. */
function CreditsMeter(props: { tenantId: string }): ReactNode {
  const credits = useCredits(props.tenantId);
  const fundedWindow = useFundedWindow(props.tenantId);
  if (credits.data === undefined) return null;
  const { available } = credits.data;
  const locale = getLocale();
  const blocked = acquisitionState(credits.data, fundedWindow.data) === 'waiting-on-credits';

  const funded = fundedWindow.data;
  const caption =
    funded === undefined || funded.funded_until === null
      ? null
      : cm(blocked ? 'meter.stopped' : 'meter.coverage', {
          date: formatDate(funded.funded_until, locale, { dateStyle: 'short', timeStyle: 'short' }),
        });

  return (
    <div className="flex flex-col gap-1.5 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span data-testid="shell-credits-available" className="text-body-sm font-medium">
          {blocked ? cm('meter.empty') : formatCredits(available, locale)}
        </span>
        <CreditsStatusIcon credits={credits.data} />
      </div>
      {caption !== null && (
        <p data-testid="shell-credits-caption" className="text-caption text-muted-foreground">
          {caption}
        </p>
      )}
      <MonthCreditsMeter credits={credits.data} />
    </div>
  );
}

/** The blocked state's primary action (Figma `186:6620`, finding `175-5249-02`):
 *  a healthy or already-partial balance shows nothing here, `AcquisitionStatus`
 *  on the page body owns those. Nothing either once a credit request is open,
 *  or on Créditos, whose own card holds that page's one request action. */
export function RequestCreditsAction(props: { tenantId: string }): ReactNode {
  const state = useAcquisitionState(props.tenantId);
  const matchRoute = useMatchRoute();
  const onCredits =
    matchRoute({
      to: '/t/$tenantId/creditos',
      params: { tenantId: props.tenantId },
      fuzzy: true,
    }) !== false;
  if (state !== 'waiting-on-credits' || onCredits) return null;
  return (
    <NoOpenRequest tenantId={props.tenantId} kind="CREDITS">
      <Button asChild size="sm">
        <Link
          to="/t/$tenantId/creditos"
          params={{ tenantId: props.tenantId }}
          search={{ pedir: 'creditos' }}
        >
          <Plus aria-hidden className="size-4" />
          {tt('balance.zero.action')}
        </Link>
      </Button>
    </NoOpenRequest>
  );
}

function hrefOf(path: string, params: Record<string, string>): string {
  return interpolatePath({ path, params }).interpolatedPath;
}

export function AppShell(props: { nav: NavEntry[]; campaignNav?: NavEntry[] }): ReactNode {
  const session = useAuthState();
  const signOut = useSignOut();
  const me = useMe();
  const context = useOrgContext();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const matchRoute = useMatchRoute();
  const href = useLocation().href;
  // Untyped for the same reason as `orgContext.ts`'s: the route tree is composed at runtime.
  const routeParams = useParams({ strict: false }) as Record<string, string>;

  // Remembers the campaign and the page within it, so the next visit to `/`
  // or to this campaign lands here (`../routes/root.tsx`, the switcher).
  const uid = session.user?.uid ?? null;
  const openCampaign = context.kind === 'campaign' ? context.campaign.campaign_id : null;
  const openTenant = context.kind === 'campaign' ? context.organisation.tenant_id : null;
  useEffect(() => {
    if (openCampaign === null || openTenant === null) return;
    writeLastPage(openCampaign, href);
    if (uid !== null) writeLastUsed(uid, { tenantId: openTenant, campaignId: openCampaign });
  }, [openCampaign, openTenant, uid, href]);

  // The URL is the only source (platform design §17.8): a page with no
  // `$tenantId`/`$campaignId` of its own gets no guessed campaign nav, and
  // the switcher -- not this shell -- is where a caller with no campaign in
  // the URL picks one.
  const tenantId =
    context.kind === 'organisation' || context.kind === 'campaign'
      ? context.organisation.tenant_id
      : null;
  // The route's own ids (`$accountId`, `$postId`, `$profileId`) let an
  // entity entry link to the entity open on screen; the organisation and
  // campaign come from the resolved context alone.
  const navParams: Record<string, string> = { ...routeParams };
  delete navParams.tenantId;
  delete navParams.campaignId;
  if (tenantId !== null) navParams.tenantId = tenantId;
  if (context.kind === 'campaign') {
    navParams.campaignId = context.campaign.campaign_id;
  }

  // Every campaign frame draws the CAMPANHA group alone: the organisation
  // entries stay for every other context, and go quiet the moment a
  // campaign is on screen. Read here too, ahead of the early returns below,
  // so the topbar's active-screen name comes from the identical filtered
  // list `Nav` renders -- two copies of this filter is the defect
  // `163-4720-05` names, not just two copies of the route-match rule.
  const orgEntries = useVisibleNavEntries(context.kind === 'campaign' ? [] : props.nav, navParams);
  const campaignEntries = useVisibleNavEntries(
    context.kind === 'campaign' ? (props.campaignNav ?? []) : [],
    navParams,
  );
  const visibleEntries = [...orgEntries, ...campaignEntries].map((entry) => ({
    path: entry.path,
    params: navParams,
    activeOn: entry.activeOn,
    label: entry.label(),
    parent: entry.parent,
  }));
  const matched = activeEntry((entry) => matchesRoute(matchRoute, entry), visibleEntries);
  // A nav entry's `parent` is the top-level entry the sidebar nests it under.
  // A child sharing its parent's path is a view of that one screen
  // (Publicações' feed and list), so the trail names the screen, not the view.
  const topLevel = (path: string) =>
    visibleEntries.find((entry) => entry.path === path && entry.parent === undefined) ?? null;
  const active =
    matched !== null && matched.parent === matched.path
      ? (topLevel(matched.path) ?? matched)
      : matched;
  const activeParent =
    active?.parent !== undefined && active.parent !== active.path ? topLevel(active.parent) : null;
  const activeIsExact = active !== null && matchesRoute(matchRoute, active, { exact: true });

  /** Every `getLocale()` reader samples the locale as it renders, and the
   *  route tree is not rebuilt by a state change up here, so the new
   *  language reaches the whole screen only on the next load. */
  function changeLocale(locale: Locale): void {
    setLocale(locale);
    window.location.reload();
  }

  // No chrome around a page that cannot load: without `/control/me` there is no
  // switcher and no navigation to render, only an empty welcome. The failure is
  // read from the taxonomy rather than from the Firebase session, so the shell
  // believes the API about who the caller is -- one source, and it is the server.
  const gate = sessionGate(session, me.error ?? null, () => void me.refetch());
  if (gate !== null) return gate;

  // The URL named a tenant or campaign absent from `organisations`
  // (platform design §17.2): no organisation here to build a sidebar or a
  // nav from, so this bypasses `Shell` entirely rather than rendering it
  // empty. Reached only by a URL or a shared link.
  if (context.kind === 'not-yours') {
    return <NotYourOrganisation />;
  }

  const name = resolveDisplayName(firebaseDisplayName(), session.user?.email ?? null);

  // Figma `186:6609`: organisation, campaign, the section, the screen.
  const crumbs: Crumb[] = [];
  if (context.kind === 'organisation' || context.kind === 'campaign') {
    crumbs.push({
      label: context.organisation.tenant_name,
      href: organisationHref(context.organisation.tenant_id),
    });
  }
  if (context.kind === 'campaign') {
    crumbs.push({
      label: context.campaign.campaign_name,
      href: hrefOf('/c/$campaignId', navParams),
    });
  }
  for (const entry of [activeParent, active]) {
    if (entry === null) continue;
    const href = hrefOf(entry.path, navParams);
    // The organisation home is both the first crumb and the screen on show:
    // the first stays as text, never a link to the page already open.
    for (const crumb of crumbs) if (crumb.href === href) delete crumb.href;
    crumbs.push({ label: entry.label, href });
  }

  return (
    <Shell
      brand={{ label: t('shell.appName'), href: '/' }}
      switcher={<OrganisationSwitcher />}
      meter={tenantId !== null ? <CreditsMeter tenantId={tenantId} /> : undefined}
      nav={
        <Nav
          entries={context.kind === 'campaign' ? [] : props.nav}
          campaignEntries={context.kind === 'campaign' ? (props.campaignNav ?? []) : []}
          params={navParams}
        />
      }
      topbar={{
        crumbs,
        exact: activeIsExact,
        actions: tenantId !== null ? <RequestCreditsAction tenantId={tenantId} /> : undefined,
      }}
      footer={
        <UserFooter
          name={name}
          onSignOut={() => void signOut()}
          onSettings={() => setSettingsOpen(true)}
        />
      }
    >
      <Outlet />
      <Toaster />
      <AccountSettings
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        name={firebaseDisplayName()}
        email={session.user?.email ?? null}
        provider={firebaseProvider()}
        onLocaleChange={changeLocale}
        onSignOut={() => void signOut()}
      />
    </Shell>
  );
}
